/**
 * The download-format table shared by the transport layer (`api-client.ts`,
 * which names the format on the wire), the pure view helpers
 * (`transcript-view-model.ts`, which names the downloaded file), and the two
 * pickers that render it. It sits in its own module so neither of those two
 * layers has to import the other for it: `api-client` needs only the type, and
 * the view model stays free of any network module.
 */
import { TRANSCRIPT_FORMAT_MIME, type TranscriptFormat } from '@kiao-voice/core'

/**
 * Transcript formats the internal-tool web page exposes. Mirrors
 * `ALLOWED_DOWNLOAD_FORMATS` in `batch_item_media_service.ts`. JSON is
 * deliberately omitted from the user-facing surface — that's a
 * machine-consumer format reserved for the public batch API — and the omission
 * is written as an exclusion so a format added to the contract surfaces here as
 * a compile error rather than as a silently missing download option.
 */
export type AudioBatchDownloadFormat = Exclude<TranscriptFormat, 'json'>

/**
 * The picker's row per format. A `Record` keyed on the union rather than a
 * hand-written array, because that is what actually makes the exclusion above
 * load-bearing: a format added to `TranscriptFormat` widens
 * `AudioBatchDownloadFormat`, and this object then fails to compile for the
 * missing key. An array would have accepted the widened type and left the new
 * format silently absent from the download menu.
 */
export const DOWNLOAD_FORMAT_ROWS: Readonly<
  Record<AudioBatchDownloadFormat, { label: string; extension: string }>
> = {
  vtt: { label: 'VTT', extension: 'vtt' },
  srt: { label: 'SRT', extension: 'srt' },
  text: { label: 'TXT', extension: 'txt' },
}

/**
 * Picker order is the key order above — insertion order, which the language
 * guarantees for non-numeric string keys. `download-formats.test.ts` pins the
 * resulting sequence, so reordering the rows is a visible change, not a silent
 * one.
 */
export const AUDIO_BATCH_DOWNLOAD_FORMATS: ReadonlyArray<{
  value: AudioBatchDownloadFormat
  label: string
  extension: string
  mime: string
}> = (Object.keys(DOWNLOAD_FORMAT_ROWS) as AudioBatchDownloadFormat[]).map((value) => ({
  value,
  ...DOWNLOAD_FORMAT_ROWS[value],
  mime: TRANSCRIPT_FORMAT_MIME[value],
}))
